import type { DecipherDatamap, RespondentRecord } from '../report-aggregation';
import { DataDownloadFile, DataExport, cellText, cellValue, fileBaseName } from './data-export';
import { plainText, withoutCounters } from './decipher-datamap';
import {
  SAV_ALIGNMENT, SAV_FORMAT_TYPE, SAV_MEASURE, SavColumn, SavDichotomySet, SavDocument, savName, writeSav,
} from './sav-writer';

/**
 * Decipher's SPSS download, written as Decipher's own .sav export is (`fixtures/data.sav`):
 * - labels are "<name>: <title>", and checkbox columns are labelled Not selected / Selected;
 * - each checkbox question is a multiple dichotomy set, labelled "<question>. <wording>";
 * - every number is ordinal and every string nominal, all with SPSS's Input role;
 * - Decipher's two timestamps are SPSS date-times;
 * - Decipher's quota counters, which its CSV carries, are left out.
 *
 * One deliberate difference: Decipher cuts text to a fixed width (open ends to 255 bytes, `markers`
 * to 64), losing what respondents wrote. Here a string is as wide as its longest answer, and SPSS's
 * very long strings carry anything over 255 bytes.
 */

type DatamapVariable = DecipherDatamap['variables'][number];
type ColumnKind = 'number' | 'dateTime' | 'text';

const PUNCH_LABELS = [{ value: 0, label: 'Not selected' }, { value: 1, label: 'Selected' }];
/** SPSS's Input role, which Decipher sets on every variable. */
const ROLE = { '$@Role': ['0'] };
const WEIGHT_TITLE = 'Respondent weight';
const DATE_TIME_COLUMNS = new Set(['date', 'start_date']);
/** Decipher's timestamps: 01/13/2026 05:35. */
const DECIPHER_TIMESTAMP = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/;
/** Seconds from SPSS's day zero, 14 October 1582, to 1 January 1970. */
const SPSS_EPOCH_OFFSET = 12219379200;
const SAV_TYPE = 'application/x-spss-sav';

const encoder = new TextEncoder();

/** A Decipher timestamp as SPSS stores a date-time: seconds since 14 October 1582. */
export function spssDateTime(timestamp: string): number | null {
  const match = DECIPHER_TIMESTAMP.exec(timestamp);
  if (!match) return null;
  const [, month, day, year, hours, minutes] = match.map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes) / 1000 + SPSS_EPOCH_OFFSET;
}

export function decipherSavDocument(download: DataExport, reportName: string, created = new Date()): SavDocument {
  const data = withoutCounters(download);
  const variables = new Map((data.datamap.variables ?? []).map(variable => [variable.label, variable]));
  const kinds = data.columns.map(column => columnKind(data, column));

  const columns = data.columns.map((column, i) =>
    savColumn(data, column, kinds[i], variables.get(column)));
  const rows = data.records.map(record => data.columns.map((column, i) => cell(data, record, column, kinds[i])));

  return {
    fileLabel: reportName,
    created,
    columns,
    rows,
    weight: data.weight?.column,
    multipleResponseSets: dichotomySets(data, kinds),
  };
}

export async function decipherSpssDownload(data: DataExport, reportName: string): Promise<DataDownloadFile> {
  const bytes = writeSav(decipherSavDocument(data, reportName));
  return { file: new Blob([bytes], { type: SAV_TYPE }), fileName: `${fileBaseName(reportName)} data.sav` };
}

/** A column is a number only if every answer in it is one; a stray word makes it text, not a blank. */
function columnKind(data: DataExport, column: string): ColumnKind {
  const answers = data.records.map(record => cellText(data, record, column)).filter(text => text !== '');
  if (DATE_TIME_COLUMNS.has(column) && answers.every(answer => DECIPHER_TIMESTAMP.test(answer))) return 'dateTime';
  if (data.numericColumns.has(column)
    && data.records.every(record => typeof cellValue(data, record, column) !== 'string')) return 'number';
  return 'text';
}

function cell(data: DataExport, record: RespondentRecord, column: string, kind: ColumnKind): number | string | null {
  if (kind === 'text') return cellText(data, record, column);
  if (kind === 'dateTime') return spssDateTime(cellText(data, record, column));
  const value = cellValue(data, record, column);
  return typeof value === 'number' ? value : null;
}

function savColumn(data: DataExport, column: string, kind: ColumnKind, variable?: DatamapVariable): SavColumn {
  const isWeight = column === data.weight?.column;
  const title = isWeight ? WEIGHT_TITLE : variable ? plainText(variable.title ?? variable.qtitle ?? '') : '';
  const label = title ? `${column}: ${title}` : column;
  const shared = { name: column, label, attributes: ROLE };

  if (kind === 'text') {
    const width = Math.max(1, ...data.records.map(record => encoder.encode(cellText(data, record, column)).length));
    const shown = Math.min(width, 255);
    return {
      ...shared,
      width,
      format: { type: SAV_FORMAT_TYPE.string, width: shown, decimals: 0 },
      measure: SAV_MEASURE.nominal,
      alignment: SAV_ALIGNMENT.left,
      displayWidth: shown,
    };
  }

  if (kind === 'dateTime') {
    return {
      ...shared,
      width: 0,
      format: { type: SAV_FORMAT_TYPE.dateTime, width: 40, decimals: 0 },
      measure: SAV_MEASURE.unknown,
      alignment: SAV_ALIGNMENT.right,
      displayWidth: 40,
    };
  }

  const valueLabels = variable?.type === 'multiple'
    ? PUNCH_LABELS
    : (variable?.values ?? []).map(value => ({ value: value.value, label: plainText(value.title ?? '') }));
  const numbers = data.records
    .map(record => cellValue(data, record, column))
    .filter((value): value is number => typeof value === 'number');
  const decimals = isWeight ? 6 : variable?.type === 'float' || numbers.some(n => !Number.isInteger(n)) ? 2 : 0;
  const digits = Math.max(1, ...[...numbers, ...valueLabels.map(label => label.value)]
    .map(n => String(Math.trunc(Math.abs(n))).length + (n < 0 ? 1 : 0)));
  const width = digits + (decimals ? decimals + 1 : 0);
  return {
    ...shared,
    width: 0,
    format: { type: SAV_FORMAT_TYPE.number, width, decimals },
    measure: isWeight ? SAV_MEASURE.scale : SAV_MEASURE.ordinal,
    alignment: SAV_ALIGNMENT.right,
    displayWidth: width,
    valueLabels,
  };
}

/** One set per checkbox question, over its columns that made it into the download as numbers. */
function dichotomySets(data: DataExport, kinds: ColumnKind[]): SavDichotomySet[] {
  const sets = new Map<string, SavDichotomySet>();
  for (const variable of data.datamap.variables ?? []) {
    if (variable.type !== 'multiple') continue;
    const index = data.columns.indexOf(variable.label);
    if (index < 0 || kinds[index] !== 'number') continue;
    const group = variable.vgroup || variable.label;
    const set = sets.get(group) ?? {
      name: savName(group),
      label: `${group}. ${plainText(variable.qtitle ?? '')}`,
      countedValue: '1',
      columns: [],
    };
    set.columns.push(variable.label);
    sets.set(group, set);
  }
  return [...sets.values()];
}
